import React, { useState, useEffect } from "react";
import {
  MapPin,
  Search,
  Plus,
  X,
  Stethoscope,
  ShoppingBag,
  Calendar,
  Home,
  Navigation,
} from "lucide-react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { LocationItem, STATIC_LOCATIONS } from "../utils/helpers";
import { useAppContext } from "../context/AppContext";

// Налаштування іконок карти
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function ChangeView({
  centerObj,
}: {
  centerObj: { coords: [number, number]; t: number };
}) {
  const map = useMap();
  useEffect(() => {
    map.flyTo(centerObj.coords, 15, { animate: true, duration: 1.2 });
  }, [centerObj, map]);
  return null;
}

function EventPickerComponent({
  onPick,
}: {
  onPick: (coords: [number, number]) => void;
}) {
  useMapEvents({
    click(e) {
      onPick([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

export default function Locations() {
  const { t, user } = useAppContext();

  const [locations, setLocations] = useState<LocationItem[]>(STATIC_LOCATIONS);
  const [mapCenterObj, setMapCenterObj] = useState<{
    coords: [number, number];
    t: number;
  }>({ coords: [50.4501, 30.5234], t: Date.now() });
  const [locCategory, setLocCategory] = useState(t.locAll);
  const [locSearch, setLocSearch] = useState("");
  const [showEventForm, setShowEventForm] = useState(false);
  const [isPickingLoc, setIsPickingLoc] = useState(false);
  const [newEventCoords, setNewEventCoords] = useState<[number, number] | null>(
    null
  );

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "locations"), orderBy("createdAt", "desc")),
      (snap) => {
        setLocations([
          ...STATIC_LOCATIONS,
          ...snap.docs.map((d) => ({ id: d.id, ...d.data() } as any)),
        ]);
      }
    );
    return () => unsub();
  }, []);

  const handleCreateEvent = async (e: any) => {
    e.preventDefault();
    if (!user || !newEventCoords) return;
    const fd = new FormData(e.currentTarget);
    await addDoc(collection(db, "locations"), {
      type: "event",
      name: fd.get("name") as string,
      address: fd.get("address") as string,
      time: fd.get("time") as string,
      coords: newEventCoords,
      distance: "Сьогодні",
      rating: 5.0,
      userId: user.uid,
      createdAt: serverTimestamp(),
    });
    setShowEventForm(false);
    setNewEventCoords(null);
    setLocCategory(t.locEvents);
    setMapCenterObj({ coords: newEventCoords, t: Date.now() });
  };

  const getLocType = (cat: string) => {
    if (cat === t.locClinics) return "clinic";
    if (cat === t.locStores) return "store";
    if (cat === t.locParks) return "park";
    if (cat === t.locEvents) return "event";
    if (cat === t.locShelters) return "shelter";
    return "all";
  };

  const filteredLocations = locations.filter(
    (l) =>
      (locCategory === t.locAll || l.type === getLocType(locCategory)) &&
      (locSearch === "" ||
        l.name.toLowerCase().includes(locSearch.toLowerCase()) ||
        l.address.toLowerCase().includes(locSearch.toLowerCase()))
  );

  return (
    <div className="flex flex-col h-[100dvh] absolute inset-0 z-20 bg-[var(--bg-main)] animate-fade-in pb-[80px]">
      {isPickingLoc && (
        <div className="absolute top-12 left-4 right-4 z-[400] bg-[var(--inverted-btn)] text-[var(--inverted-text)] p-4 rounded-2xl shadow-xl flex justify-between items-center animate-fade-in">
          <span className="text-sm font-bold flex items-center gap-2">
            <MapPin size={18} /> {t.clickMap}
          </span>
          <button
            onClick={() => setIsPickingLoc(false)}
            className="p-1.5 bg-white/20 rounded-full active:scale-95"
          >
            <X size={16} />
          </button>
        </div>
      )}
      <div className="bg-[var(--bg-card)] border-b border-[var(--border-color)] px-5 py-4 shrink-0 pt-12 shadow-sm flex flex-col z-30 relative">
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-extrabold text-xl flex items-center gap-2 text-[var(--text-main)]">
            <MapPin size={22} className="text-[var(--red-text)]" /> {t.locs}
          </h2>
        </div>
        <div className="flex items-center gap-2 bg-[var(--bg-soft)] rounded-xl px-3 py-2 border border-[var(--border-color)] mb-3">
          <Search size={16} className="text-[var(--text-muted)]" />
          <input
            value={locSearch}
            onChange={(e) => setLocSearch(e.target.value)}
            placeholder={t.searchLoc}
            className="bg-transparent outline-none text-sm w-full font-medium text-[var(--text-main)]"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {[
            t.locAll,
            t.locClinics,
            t.locStores,
            t.locParks,
            t.locEvents,
            t.locShelters,
          ].map((cat) => (
            <button
              key={cat}
              onClick={() => setLocCategory(cat)}
              className={`shrink-0 px-4 py-1.5 rounded-xl text-xs font-bold border transition-all ${
                locCategory === cat
                  ? "bg-[var(--inverted-btn)] text-[var(--inverted-text)] border-[var(--inverted-btn)]"
                  : "bg-[var(--bg-soft)] text-[var(--text-muted)] border-[var(--border-color)]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 relative z-10">
        <MapContainer
          center={mapCenterObj.coords}
          zoom={13}
          style={{ height: "100%", width: "100%" }}
          zoomControl={false}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <ChangeView centerObj={mapCenterObj} />
          {isPickingLoc && (
            <EventPickerComponent
              onPick={(coords) => {
                setNewEventCoords(coords);
                setIsPickingLoc(false);
                setShowEventForm(true);
              }}
            />
          )}
          {filteredLocations.map((loc) => (
            <Marker key={loc.id} position={loc.coords}>
              <Popup>
                <div className="font-bold text-sm text-[#2d1f45]">
                  {loc.name}
                </div>
                <div className="text-[10px] text-[#2d1f45]">{loc.address}</div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      <div className="h-[40%] bg-[var(--bg-card)] rounded-t-[2rem] shadow-2xl p-5 overflow-y-auto no-scrollbar relative z-20 -mt-8 border-t border-[var(--border-color)] flex flex-col">
        <div className="w-12 h-1.5 bg-[var(--border-color)] rounded-full mx-auto mb-5 shrink-0" />
        <div className="flex-1 overflow-y-auto no-scrollbar">
          {filteredLocations.length === 0 && (
            <div className="text-center text-[var(--text-muted)] text-sm font-bold mt-10">
              {t.noLocs}
            </div>
          )}
          {filteredLocations.map((loc) => (
            <div
              key={loc.id}
              onClick={() =>
                setMapCenterObj({ coords: loc.coords, t: Date.now() })
              }
              className="flex gap-4 p-4 bg-[var(--bg-soft)] rounded-2xl border border-[var(--border-color)] mb-3 items-center active:scale-95 transition-all cursor-pointer"
            >
              <div className="w-12 h-12 rounded-xl bg-[var(--bg-card)] text-[#a073e7] flex items-center justify-center shadow-sm border border-[var(--border-color)]">
                {loc.type === "clinic" ? (
                  <Stethoscope size={24} />
                ) : loc.type === "store" ? (
                  <ShoppingBag size={24} />
                ) : loc.type === "event" ? (
                  <Calendar size={24} />
                ) : loc.type === "shelter" ? (
                  <Home size={24} className="text-[var(--red-text)]" />
                ) : (
                  <Navigation size={24} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm truncate text-[var(--text-main)]">
                  {loc.name}
                </h4>
                <p className="text-[10px] text-[var(--text-muted)]">
                  {loc.address} • {loc.distance}
                </p>
                {loc.time && (
                  <p className="text-[10px] font-bold text-[var(--red-text)] mt-1">
                    {loc.time}
                  </p>
                )}
              </div>
              <div className="p-2 bg-[var(--bg-card)] border border-[var(--border-color)] rounded-full text-[#a073e7] shadow-sm">
                <Navigation size={18} />
              </div>
            </div>
          ))}
        </div>
        {locCategory === t.locEvents && !isPickingLoc && (
          <button
            onClick={() => setIsPickingLoc(true)}
            className="w-full mt-2 py-3 bg-[var(--inverted-btn)] text-[var(--inverted-text)] font-bold rounded-xl flex items-center justify-center gap-2 shadow-md active:scale-95 transition-all shrink-0"
          >
            <Plus size={18} /> {t.createEvent}
          </button>
        )}
      </div>

      {showEventForm && (
        <div className="fixed inset-0 bg-black/60 z-[400] flex flex-col justify-end backdrop-blur-sm animate-fade-in">
          <div className="bg-[var(--bg-card)] rounded-t-[2.5rem] p-6 pb-12 animate-slide-up border-t border-[var(--border-color)]">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-extrabold text-xl text-[var(--text-main)]">
                {t.modNewEvent}
              </h3>
              <button
                onClick={() => setShowEventForm(false)}
                className="text-[var(--text-main)]"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-xs text-[var(--text-muted)] mb-4">
              {t.modEvDesc}
            </p>
            <form onSubmit={handleCreateEvent} className="flex flex-col gap-4">
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] uppercase block mb-2">
                  {t.modEvName}
                </label>
                <input
                  name="name"
                  required
                  className="w-full h-12 px-4 bg-[var(--bg-soft)] border border-[var(--border-color)] text-[var(--text-main)] rounded-xl outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] uppercase block mb-2">
                  {t.modEvPlace}
                </label>
                <input
                  name="address"
                  required
                  className="w-full h-12 px-4 bg-[var(--bg-soft)] border border-[var(--border-color)] text-[var(--text-main)] rounded-xl outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] uppercase block mb-2">
                  {t.modEvTime}
                </label>
                <input
                  name="time"
                  required
                  className="w-full h-12 px-4 bg-[var(--bg-soft)] border border-[var(--border-color)] text-[var(--text-main)] rounded-xl outline-none"
                />
              </div>
              <button
                type="submit"
                className="w-full h-14 bg-[var(--inverted-btn)] text-[var(--inverted-text)] font-bold rounded-xl mt-4 shadow-lg active:scale-95 transition-all"
              >
                {t.createEvent}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
